import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Dropzone from 'react-dropzone';
import { noop, isEmpty, every } from 'lodash';
import { FILE_UPLOADER_ERRORS } from 'constants/common';
import * as imageDimensions from 'utils/imageDimensions';

// import default view
import { fileUploader as FileUploaderView } from './fileUploader.view';

/**
 * @type {function}
 * @desc A Higher-Order-Component to enhance functionality of `Component`
 * @param {object} Component - A React component
 * @example
 * import { fileUploader, FileUploader } from 'modules/fileUploader';
 *
 * // create a component
 * const ComponentWithHOC = fileUploader( CustomView );
 */
const fileUploader = ( Component = FileUploaderView ) => {
    class FileUploader extends React.Component {
        constructor( props ) {
            super( props );

            // component state
            this.state = {
                isUploading: false,
                description: this.props.description
            };

            // bind methods to the instance
            this.handleDrop = this.handleDrop.bind( this );
        }

        /**
         * @desc validate attached files
         */
        async validateFiles( ...files ) {
            
            // check if image dimenstions and aspect ratio are within constraints
            if( ! isEmpty( this.props.dimensions ) /*|| ! isEmpty( this.props.aspectRatio )*/ ) {
                try {

                    // get image dimensions of each file
                    const dimensionsList = await Promise.all( files.map( file => imageDimensions.fromFile( file ) ) );
    
                    // check image dimensions
                    if( ! isEmpty( this.props.dimensions ) ) {
                        if( ! every( dimensionsList, ( dimensions ) => {
                            return dimensions.width >= this.props.dimensions.minWidth && dimensions.width <= this.props.dimensions.maxWidth && dimensions.height >= this.props.dimensions.minHeight && dimensions.height <= this.props.dimensions.maxHeight;
                        } ) ) {
                            return {
                                type: FILE_UPLOADER_ERRORS.INVALID_FILE_DIMENSIONS,
                                data: null,
                            };
                        }
                    }

                    // check aspect ratio
                    // if( isNumber( this.props.aspectRatio ) && 0 < this.props.aspectRatio ) {
                    //     if( ! every( dimensionsList, ( dimensions ) => {
                    //         return ( dimensions.width / dimensions.height ) === this.props.aspectRatio;
                    //     } ) ) {
                    //         return {
                    //             type: FILE_UPLOADER_ERRORS.INVALID_ASPECT_RATIO,
                    //             data: null,
                    //         };
                    //     }
                    // }
                    
                } catch {
                    return true; // valid in case dimensions can't be found
                }
            }

            /*--------------------*/

            // return `null` error
            return null;

        }

        /**
         * @desc Callback function to handle file drop on the dropzone.
         */
        async handleDrop( files ) {
            log.debug( 'FileUploader.handleDrop()' );

            if( 0 < files.length ) {

                // validate attached files
                const validationError = await this.validateFiles( ...files );

                if( null === validationError ) {
                    this.setState( {
                        isUploading: true,
                    }, async () => {

                        // upload start callback
                        this.props.onUploadStart();

                        // execute upload service
                        try {
                            const response = await this.props.service( ...files );
                            this.props.onUploadSuccess( response ); // execute success callback
                        } catch ( error ) {
                            return this.props.onUploadFailure( { type: FILE_UPLOADER_ERRORS.SERVER_ERROR, data: error, } ); // execute error callback
                        } finally {
                            this.setState( {
                                isUploading: false,
                            } );
                        }
                    } );
                } else {

                    return this.props.onError( validationError );
                }
            }
        }
        
        /**
         * @desc Render the trigegr view with dropzone.
         */
        render() {
    
            // get class name and props
            const { className, ...restProps } = this.props;

            const disabled = this.props.disabled || this.state.isUploading || ( !this.props.multiple ? this.props.value && 1 === this.props.value.length : false );

            // dynamic class name
            const widgetClassName = classnames(
                'ui-file-uploader',
                className,
                { 'ui-file-uploader--disabled': disabled }
            );
    
            return (
                <Dropzone
                    accept={ this.props.accept }
                    noDrag={ ! this.props.isDragAllowed }
                    disabled={ disabled }
                    multiple={ this.props.multiple }
                    maxSize={ this.props.maxSize }
                    minSize={ this.props.minSize }
                    onDropRejected={ () => this.props.onError( { type: FILE_UPLOADER_ERRORS.INVALID_FILE, } ) } // invalid file size or file type
                    onDrop={ this.handleDrop }
                >
                    { ( { getRootProps, getInputProps } ) => (
                        <div className={ widgetClassName } { ...getRootProps() }>
    
                            {/* file input */}
                            <input { ...getInputProps() } />
                            
                            {/* default trigger view */}
                            <Component className='ui-file-uploader__trigger' isUploading={ this.state.isUploading } { ...restProps }/>
                        </div>
                    ) }
                </Dropzone>
            );
        }
    }
    
    // set display name
    FileUploader.displayName = 'FileUploader';
    
    // default props
    FileUploader.defaultProps = {
        noDrag: false,
        disabled: false,
        multiple: false,
        accept: [ 'image/*' ], // defalt: [ .png, .jpg, ... ]
        maxSize: 5 * 1000 * 1000, // max: 5MB
        minSize: 1000, // min: 1KB
        dimensions: undefined,
        aspectRatio: undefined,
        service: noop,
        onError: noop,
        onUploadStart: noop,
        onUploadSuccess: noop,
        onUploadFailure: noop,
    };
    
    // prop types
    FileUploader.propTypes = {
        noDrag: PropTypes.bool, // avoid file drag
        disabled: PropTypes.bool, // disable file upload
        multiple: PropTypes.bool, // allow uploading multiple files
        accept: PropTypes.arrayOf( PropTypes.string ), // accepted MIME types
        maxSize: PropTypes.number, // maximum file size in bytes
        minSize: PropTypes.number, // minimum file size in bytes
        dimensions: PropTypes.object, // resolution constraints of an image file
        aspectRatio: PropTypes.number, // aspect ratio constraints of an image file
        service: PropTypes.func, // a service function when invoked with attached files sends a AJAX request returns a Promise
        onError: PropTypes.func, // if attach files are invalid
        onUploadStart: PropTypes.func, // when upload is started
        onUploadSuccess: PropTypes.func, // callback for upload success
        onUploadFailure: PropTypes.func, // callback for upload failure
    };

    // return class
    return FileUploader;
};

// export file uploader with default trigger view
export const FileUploader = fileUploader();
